import type { ReactNode } from "react";
import { Card } from "../../ui/card";
import { cn } from "../../utils/common/cn";

interface StatCardProps {
  label: string;
  value: ReactNode;
  sublabel?: ReactNode;
  accent?: "default" | "amber" | "green" | "red";
  className?: string;
  /** For cards laid out side by side in one grid (the Capacity cards): the label, the value and the sublabel each take a row that is
   *  shared with the neighbouring cards, so when one label wraps onto two lines the others leave the same space and every value (and
   *  every sublabel) starts at the same height. Only meaningful as a direct child of a CSS grid. */
  aligned?: boolean;
}

const ACCENT_CLASS: Record<NonNullable<StatCardProps["accent"]>, string> = {
  default: "text-foreground",
  amber: "text-amber",
  green: "text-[color:var(--approve)]",
  red: "text-destructive",
};

export function StatCard({ label, value, sublabel, accent = "default", className, aligned }: StatCardProps) {
  return (
    // aligned: the card spans three of the grid's rows and hands them to its label / value / sublabel (CSS subgrid), so those rows are
    // sized once for the whole grid row. The empty third div keeps three children when there is no sublabel.
    <Card className={cn("gap-0 p-4", aligned && "row-span-3 grid gap-y-0 [grid-template-rows:subgrid]", className)}>
      <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </div>
      <div className={cn("mt-2 text-2xl font-semibold tabular-nums", ACCENT_CLASS[accent])}>
        {value}
      </div>
      {sublabel ? <div className="mt-1 text-xs text-muted-foreground">{sublabel}</div> : aligned ? <div /> : null}
    </Card>
  );
}
